import React, { useMemo, useState } from "react";
import getConfig from "next/config";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { UseComboboxStateChange } from "downshift"; // eslint-disable-line import/no-extraneous-dependencies

import {
    ComposableHeaderDropdownProviderProps,
    Header,
    IndexedObject,
    PageLayout,
    Search,
    useSessionStorage,
} from "@themuse/design-system";
import {
    initSnowplowTracker,
    trackSnowplowImpressions,
    triggerSnowplowPageViewEvent,
    trackSnowplowClicks,
    trackSnowplowModalActions
} from "@themuse/snowplow-js-client";
import {
    ExperimentProvider,
    getActiveVariant,
    getExperiments,
    IExperiment,
} from "@themuse/experiment-client";

import { UserActionTypes, PageMetaDataActionTypes } from "interfaces/context/appContextInterfaces";
import { pushDataLayerEvent } from "utils/tracking";
import { isBrowser } from "utils/browser";
import { getActiveFeatureFlags } from "utils/featureFlags";
import { formatLog } from "utils/logging";
import { getCanonicalUrl } from "utils/strings";
import {
    AppArticle,
    getCompressedArticle
} from "utils/orm";
import {
    newsletterSubscribeRequest,
    fetchUser, getOptimizedPosts,
    ApiArticle, CompanySuggestion,
    fetchCompanySuggestions,
    fetchApiCarmenData
} from "utils/api";

import { AppProvider, useAppState, useAppDispatch } from "hooks/AppContext";
import { FeatureFlagsProvider } from "hooks/FeatureFlagsContext";
import { AppPageLayout } from "components/AppPageLayout";
import { ArticleContainer } from "components/ArticleContainer";
import { SubNav } from "components/SubNav";
import { SEO } from "components/SEO";
import { getFetchError } from "utils/errors";

import styles from "styles/Home.module.scss";
import { MayaBanner } from "components/MayaBanner/MayaBanner";

interface InitialAppParams {
    article: AppArticle;
    path: string;
    experiments: IExperiment[],
    enabledVariant: string,
}

/**
 * Experiment configurations.
 */
const experimentConfig: IExperiment[] = [];

/**
 * Fetches server-side props for the page, handling redirects and 404 errors.
 *
 * @param context - The server-side context provided by Next.js, containing request and query information.
 * @returns An object containing either a redirect, a notFound flag, or the initial app parameters with experiments and feature flags.
 *
 * - Redirects malformed URLs and CMS redirects with a 308 status.
 * - Returns a 404 status for non-existent articles or invalid URLs.
 * - Retrieves initial application parameters and active feature flags.
 */
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    let initialAppParams: InitialAppParams = {
        article: null,
        path: "",
        experiments: [],
        enabledVariant: "",
    };

    // Let's fetch the article props and see what we get.
    const articleParams = await getInitialAppParams(context);

    // occasionally we get urls that are malformed. such as social media links missing the `?` before the query string
    // Permanently redirect those to the correct page.
    if (context.query?.articleUri.includes("&")) {
        const destination = (context.query?.articleUri as unknown as string ?? "").split("?")[0].split("&")[0];
        const response = {
            redirect: {
                destination,
                permanent: true,
            }
        };
        return response;
    }


    // This article can never exist.
    // https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#notfound
    const redirect404 = [
        (/\/advice\/[^\w]/i).test(context.req.url),
        (/^http/i).test(context.req.url)
    ];
    if (redirect404.some(Boolean)) {
        const response = {
            notFound: true
        };
        return response;
    }

    // We received a redirect from the CMS so this is a 308.
    // https://nextjs.org/docs/api-reference/next.config.js/redirects
    if (initialAppParams?.article?.meta?.status === "redirect") {
        const response =  {
            redirect: {
                destination: initialAppParams.article.meta?.location,
                permanent: true,
            }
        };
        return response;
    }

    // This article does not exist in the CMS so this is a 404.
    // https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#notfound
    if (!articleParams?.article?.id) {
        const response = {
            notFound: true
        };
        return response;
    }

    const { experiments = [] } = initialAppParams;

    const response = {
        props: {
            ...initialAppParams,
            ...articleParams,
            experiments,
            featureFlags: {
                ...getActiveFeatureFlags(context),
            }
        }
    };
    return response;
};

/**
 * Retrieves initial parameters for the application, including article data and experiment configurations.
 *
 * @param context - The server-side context containing query parameters and request information.
 * @returns A promise that resolves to an object containing the compressed article, the resolved URL path,
 *          active experiments, and the enabled variant.
 *
 * This function fetches and processes article data based on the provided URI and token from the context query.
 * It handles malformed URLs by pruning unnecessary query strings. It also retrieves active experiments and
 * determines the enabled variant for a specific experiment. In case of an error during article retrieval,
 * it logs a warning message.
 */
const getInitialAppParams = async (context: GetServerSidePropsContext): Promise<InitialAppParams> => {
    const {
        publicRuntimeConfig: { activeExperiments }
    } = getConfig();
    const experiments: IExperiment[] = getExperiments(experimentConfig, context, activeExperiments) ?? [];
    const enabledVariant: string = getActiveVariant("XXXXX", experiments) ?? "";
    const { articleUri, token, "x-craft-live-preview": preview } = context.query;

    // occasionally we get urls that are malformed. such as social media links with the query string
    const prunedUri = (articleUri as unknown as string ?? "").split("?")[0].split("&")[0];
    let apiArticle: ApiArticle = null;
    try {
        const uri = `advice/${prunedUri}`;
        const response = await getOptimizedPosts({ uri, token, preview });
        const { meta, data } = response;
        const { type } = meta || {};

        apiArticle = {
            ...data,
            meta,
            templateName: type === "listicles" ? "listicle" : "article"
        };
    } catch (error) {
        const msg = `Article Renderer was unable to retrieve the post advice/${articleUri}`;
        console.warn(msg, JSON.stringify(formatLog({ context, error })));
    }

    const article = await getCompressedArticle(apiArticle);

    return {
        article,
        path: context.resolvedUrl,
        experiments,
        enabledVariant,
    };
};

/**
 * Asynchronously fetches Carmen location data and updates the user location state.
 *
 * @param setUserLocation - A function to update the user location state with the fetched Carmen data.
 *
 * Fetches data using `fetchApiCarmenData` and, if successful, sets the user location
 * with the JSON stringified response. Logs an error message if the fetch fails.
 */
const setUserCarmenData = async (setUserLocation: any) => {
    try {
        const carmenResponse = await fetchApiCarmenData();
        if (carmenResponse) {
            setUserLocation(JSON.stringify(carmenResponse));
        }
    } catch (error) {
        const msg = getFetchError("Carmen Location", "Front-End");
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * Asynchronously sets the user data in the application state.
 *
 * Fetches user information from the API and updates the application state
 * with the user's location and login status. If an error occurs during the
 * fetch operation, logs the error with a formatted message.
 *
 * @param {string | null} userLocation - The user's location in JSON string format.
 * @param {Function} dispatch - The dispatch function to update the application state.
 */
const setUser = async (userLocation, dispatch) => {
    try {
        const apiUser = await fetchUser();
        const userData = userLocation ? {
            location: JSON.parse(userLocation)
        } : {};
        dispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...(apiUser?.logged_in && apiUser),
                ...userData,
                isInitialized: true,
            }
        });
    } catch (error) {
        const msg = getFetchError("User", "Front-End");
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * UserInit is a React functional component that initializes user-related data
 * and manages user location state using session storage. It dispatches user
 * updates to the application state and sets user Carmen data on component mount.
 *
 * @param {React.PropsWithChildren<{}>} props - The component's children elements.
 * @returns {JSX.Element} The rendered children elements.
 *
 * @remarks
 * - Utilizes `useAppState` to access the current user state.
 * - Uses `useSessionStorage` to persist user location across sessions.
 * - Dispatches user updates using `useAppDispatch`.
 * - Employs `useEffect` hooks to handle side effects related to user location
 *   and Carmen data initialization.
 */
const UserInit: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { user } = useAppState();
    const [userLocation, setUserLocation] = useSessionStorage("user_location");
    const appDispatch = useAppDispatch();

    React.useEffect(() => {
        setUser(userLocation, appDispatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation]);

    React.useEffect(() => {
        setUserCarmenData(setUserLocation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        appDispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...user,
                location: userLocation ? JSON.parse(userLocation) : user?.location
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLocation]);

    return <>{ children }</>;
};

/**
 * PageMetaInit is a React functional component that initializes and updates
 * the page metadata in the application state using the current window location
 * and document referrer. It dispatches an action to update the page metadata
 * when the component mounts.
 *
 * @param {React.PropsWithChildren<{}>} props - The component's children.
 * @returns {JSX.Element} The rendered children components.
 */
const PageMetaInit: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { pageMetaData } = useAppState();
    const appDispatch = useAppDispatch();

    React.useEffect(() => {
        const { href, search } = window.location;
        const { referrer } = document;
        appDispatch({
            type: PageMetaDataActionTypes.updatePageMetaData,
            payload: {
                ...pageMetaData,
                ...{
                    href,
                    search,
                    referrer
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>{ children }</>;
};

/**
 * Handles server-side logic for fetching initial application parameters and managing redirects.
 *
 * @param {GetServerSidePropsContext} context - The context object containing request details.
 * @returns {Promise<{ redirect?: { destination: string, permanent: boolean }, notFound?: boolean, props?: InitialAppParams }>}
 * - Returns a redirect object for malformed URLs or CMS redirects, a notFound flag for non-existent articles,
 *   or props containing initial application parameters and feature flags.
 *
 * The function checks for malformed URLs and redirects them permanently to the correct page.
 * It also handles 404 errors for non-existent articles and processes CMS redirects.
 * If the article is found, it returns the initial application parameters and active feature flags.
 */
const SnowPlowInit = () => {
    const { publicRuntimeConfig: { snowplowCollector } } = getConfig();
    const { user, article: { sponsor, id: articleId } } = useAppState();
    const [pageViewed, setPageViewed] = useState<boolean>(false);
    const { legacyId: companySponsorId } = sponsor;

    const pageData = useMemo(() => ({
        sp_page_is_external: false,
        sp_page_section: "advice",
        sp_page_sponsor_id: companySponsorId?.toString() || null,
        sp_page_tab: null,
        sp_page_type: "content",
        sp_page_entity_id: articleId.toString() || null,
        sp_page_entity_uuid: null,
        sp_user_logged_in: user?.logged_in,
        sp_user_id: user?.id,
        sp_experiment_experiment_id: "",
        sp_experiment_variant_id: ""
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    React.useEffect(() => {
        if (!pageViewed) {
            // eslint-disable-next-line no-underscore-dangle
            const enabledVariant = getActiveVariant("XXXXX", window.__NEXT_DATA__.props.pageProps.experiments);
            const experimentData = enabledVariant
                ? {
                    sp_experiment_experiment_id: "XXXXX",
                    sp_experiment_variant_id: enabledVariant
                }
                : {};

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                ...pageData,
                ...experimentData
            });
            initSnowplowTracker(snowplowCollector);
            trackSnowplowImpressions();
            trackSnowplowClicks();
            trackSnowplowModalActions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (user.isInitialized && !pageViewed) {
            triggerSnowplowPageViewEvent();
            setPageViewed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    return null;
};

/**
 * Formats an array of company suggestions by mapping each suggestion to an object
 * containing a label and a value. The label is derived from the suggestion's text,
 * and the value is a URL path constructed using the suggestion's short_name.
 * Only the first four suggestions are processed.
 *
 * @param {CompanySuggestion[]} companySuggestions - The array of company suggestions to format.
 * @returns {Array<{ label: string, value: string }>} An array of formatted company suggestion objects.
 */
export const formatCompanySuggestions = (companySuggestions: CompanySuggestion[]) => companySuggestions.slice(0, 4).map((companyResult: CompanySuggestion) => {
    const { text, value } = companyResult;
    const { short_name } = value; // eslint-disable-line camelcase
    return {
        label: `${text}`,
        value: `/profiles/${short_name}` // eslint-disable-line camelcase
    };
});

/**
 * Updates the input value and fetches company suggestions based on the input.
 *
 * @param changes - The state changes from the combobox, including the new input value.
 * @param setItems - Function to update the list of items displayed in the combobox.
 * @param setInputValue - Function to update the current input value in the combobox.
 * @param fetchData - Asynchronous function to fetch company suggestions based on the input value.
 *
 * The function sets the input value and updates the items in the combobox.
 * If the input value is not empty, it fetches company suggestions and updates the items list.
 * If the input value is empty, it clears the items list.
 */
export const updateInput = async (
    changes: UseComboboxStateChange<IndexedObject>,
    setItems: (items: IndexedObject[]) => void,
    setInputValue: (inputValue: string) => void,
    fetchData:(inputValue: string) => Promise<CompanySuggestion[]>
) => {
    const { inputValue } = changes;
    const jobSearchItem = {
        label: `${inputValue}`,
        value: `/search/keyword/${inputValue}/`,
    };
    setInputValue(inputValue);
    if (inputValue) {
        setItems([jobSearchItem]);
        const companyResults = await fetchData(inputValue);
        const companyList = companyResults?.length ? formatCompanySuggestions(companyResults) : [];
        setItems([jobSearchItem].concat(companyList));
    } else {
        setItems([]);
    }
};

/**
 * Renders the ArticlePage component, which displays an article with its metadata,
 * search functionality, and advertisements based on certain conditions.
 *
 * @param props - The server-side props containing article data, feature flags,
 * experiments, and the current path.
 * @returns A JSX element representing the article page layout with various
 * components such as SEO, MediaVine ads, and article content.
 */
const ArticlePage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const {
        article, featureFlags, experiments, path
    } = props;
    const {
        articleUri,
        contentAuthor,
        breadcrumbLinks,
        dateUpdated,
        excerpt,
        leadMedia,
        postDate,
        primaryTopic,
        seo,
        sponsor,
        product,
        title,
        tags
    } = article;
    const {
        writerName,
        writerUri,
    } = contentAuthor ?? {};
    const canonical = getCanonicalUrl(path);
    const description = excerpt;
    const pageMetaData = {
        pathName: path
    };
    const [dropdownItems, setDropdownItems] = useState([] as IndexedObject[]);
    const [inputValue, setInputValue] = useState("");

    const searchQuerySubmittedEvent = (selectedItemText: string) => {
        const searchTerm = selectedItemText || inputValue;
        const eventProps = {
            search_term: searchTerm || ""
        };
        pushDataLayerEvent("searchQuerySubmitted", eventProps);
    };

    const onKeyboardSelectItem = (_: React.KeyboardEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        if (!isBrowser()) return "";

        const searchPath = inputValue
            ? `/search/keyword/${inputValue}`
            : "/search";
        const selectPath = selectedItem?.value
            ? selectedItem?.value
            : searchPath;

        searchQuerySubmittedEvent(selectedItem?.label);

        return window.open(selectPath, "_blank").focus();
    };

    const onMouseSelectItem = (_: React.MouseEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        searchQuerySubmittedEvent(selectedItem.label);
    };

    const dropdownProps: ComposableHeaderDropdownProviderProps = {
        IconComponent: <Search />,
        debounceWait: 10,
        dropdownList: dropdownItems,
        inputId: "search-input",
        onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => updateInput(
            changes,
            setDropdownItems,
            setInputValue,
            fetchCompanySuggestions
        ),
        noResultsMessage: " ",
        onKeyboardSelectItem,
        onMouseSelectItem
    };

    const pageLayoutProps: React.ComponentProps<typeof PageLayout> & React.PropsWithChildren<React.ComponentProps<typeof Header>> = {
        activePath: "/advice",
        headerClassName: styles.pageHeader,
        pageName: styles.pageWrapper,
        mainClassName: styles.main,
        enableCompanySearch: true,
        companySearchDropdownProps: dropdownProps,
        showSubMenus: true,
        handleModalFormSubmit: newsletterSubscribeRequest
    };

    /**
     * To have ads you must:
     * * Not be a roundup
     * * Not be tagged as vibe-awards
     * * Not be sponsored.
     */
    const hasAds = !/roundup/.test((product as undefined as any)?.slug ?? false)
        && !(sponsor as undefined as any)?.hasSponsorShip
        && !(tags as undefined as any[]).filter((x) => x?.slug?.includes("vibe-awards"))?.length;

    return (
        <ExperimentProvider experiments={experiments}>
            <FeatureFlagsProvider featureFlags={featureFlags}>
                <AppProvider {...{ article, pageMetaData }}>
                    <SEO {...{
                        articleUri,
                        canonical,
                        breadcrumbLinks,
                        dateUpdated,
                        description,
                        leadMedia,
                        postDate,
                        primaryTopic,
                        seo,
                        title,
                        writerName,
                        writerUri,
                    }}
                    />
                    <UserInit>
                        <PageMetaInit>
                            <SnowPlowInit />
                            <AppPageLayout {...pageLayoutProps}>
                                <MayaBanner />
                                <SubNav />
                                <ArticleContainer />
                            </AppPageLayout>
                        </PageMetaInit>
                    </UserInit>
                </AppProvider>
            </FeatureFlagsProvider>
        </ExperimentProvider>
    );
};

export default ArticlePage;
